/**
 * 外部予約サイトへの「条件プリセット済みディープリンク」生成（純粋関数）。
 *
 * 入力は src/lib/trip.ts の TripConditions ただ1つ（出発日・帰着日・大人人数・部屋数）＋射場/空港。
 * 画面で1回入れた条件をそのままURLに載せて各予約サイトへ渡すのがこのファイルの仕事で、
 * 「先方のサイトで日付と場所をもう一度入れ直す」を無くすのが目的。
 *
 * ★方針（このリポの規律・破ると実害が出ている）: URLパラメータ仕様は**推測しない**。
 *   実URL／サイト内リンク／実描画で確認できたものだけ prefilled=true。
 *   確認できなかったサービスはトップ/検索LP＋「手動入力」注記（prefilled=false）。
 *   - **HTTP 200 は証拠にならない。** 200のまま静かに別条件へ化ける実例を今回も踏んでいる
 *     （アソビューで destinationType を推測すると全国4,013件になる／じゃらんは200のまま「エラー画面」）。
 *   - 検証の証跡: docs/PHASE4-DEEPLINK-FINDINGS.md（2026-08-01に全面更新）。
 *
 * ★2026-08-01の再検証で、過去の判定のうち**4件が誤りだった**と判明し訂正した:
 *   1. 楽天レンタカー「rcf010a.do はエンドポイント廃止で接続拒否」→ **誤り**。
 *      cars.travel.rakuten.co.jp は curl を弾く（HTTP/2 INTERNAL_ERROR／--http1.1 はハング）だけで、
 *      実ブラウザからは200で正常動作する。**curl由来の偽陰性**だった。
 *   2. じゃらんレンタカー「サブエリアコードが導出不可・県のみだと羽田に黙って落ちる」→ **誤り**。
 *      コードはフォームとサイト内リンクから全部採取でき、県単独のURL形式はそもそも存在しない。
 *   3. アソビュー「県別URLの形式を特定できなかった」→ **誤り**。`/area/` ではなく `/location/` 配下。
 *   4. るるぶ `/city/japan/<slug>` に日付クエリを足せば効く → **効かない**（200のまま日付が入らない）。
 *      日付を渡すなら `/search?city=<数値ID>` 系へ。
 *   教訓: 「不在」の判定はツールの都合で簡単に偽になる（[[verification-false-absence]]）。
 */
import type { TripConditions } from './trip';
import { diffDays, todayIsoJst } from './trip';
import type { Airport, LaunchSite } from '../data/launch-sites';

export interface DeepLink {
  serviceId: string;
  label: string;
  url: string;
  /** 貸出/返却日など条件がプリセット済みか（false=手動入力） */
  prefilled: boolean;
  /** 補足（プリセット内容 or 「手動入力」など） */
  note: string;
}

// ---- 日付ヘルパー（ISO "2026-09-10" 前提・すべてゼロ埋め済みの文字列を返す） ----
function ymd(iso: string): { y: string; m: string; d: string } {
  const [y, m, d] = iso.split('-');
  return { y: y ?? '', m: m ?? '', d: d ?? '' };
}
/** "2026-09-10" → "260910"（Skyscannerのyymmdd） */
function yymmdd(iso: string): string {
  const { y, m, d } = ymd(iso);
  return `${y.slice(2)}${m}${d}`;
}
/** "2026-09-10" → "1009"（Aviasalesの検索コードは**日→月**の順。実測: 0508 → "05.08"＝8月5日） */
function ddmm(iso: string): string {
  const { m, d } = ymd(iso);
  return `${d}${m}`;
}
/** "2026-09-10" → "20260910"（アクティビティジャパンの joinDayFromCalendar） */
function compactYmd(iso: string): string {
  const { y, m, d } = ymd(iso);
  return `${y}${m}${d}`;
}
/** "2026-09-10" → "2026年9月10日"（Googleホテルの `q=` に混ぜる和文日付。ゼロ埋めしない） */
function jaDate(iso: string): string {
  const { y, m, d } = ymd(iso);
  return `${y}年${Number(m)}月${Number(d)}日`;
}
/** 射場名から括弧書き（「（吉信射点 LA-Y）」など）を落とした短い名前。 */
function shortSiteName(site: LaunchSite): string {
  return site.nameJa.replace(/（.*/, '');
}
/** 「9/10〜9/13」 */
function rangeLabel(trip: TripConditions): string {
  const a = ymd(trip.departIso);
  const b = ymd(trip.returnIso);
  return `${Number(a.m)}/${Number(a.d)}〜${Number(b.m)}/${Number(b.d)}`;
}
function nights(trip: TripConditions): number {
  return Math.max(1, diffDays(trip.departIso, trip.returnIso));
}

/**
 * 楽天レンタカーの在庫がある実用範囲（日数）。
 * 実測（2026-08-01）: 2027-03 までは在庫あり、2027-06 は条件が正しく反映されたうえで
 * 「指定のボディタイプの在庫が存在しません」＝**約8か月先までが実用範囲**。
 * ロケットの打ち上げ予定は1年以上先も普通にあるので、遠い日程で
 * 「条件プリセット」と称して0件ページへ送らないためのガードに使う。
 */
export const RAKUTEN_CAR_MAX_LEAD_DAYS = 240;

/**
 * 楽天レンタカーの車種（ボディタイプ）チェックボックス全9値。
 * ★1つも渡さないと、日付も場所も正しく描画されるのに**一覧だけが空**になる（本文「処理中にエラーが
 *   発生しました。」／HTTP200・タイトルも正常）。今回見つかった中で唯一の「静かめな失敗」なので必ず全部付ける。
 */
const RAKUTEN_CAR_BODY_TYPES = [1, 2, 3, 4, 5, 6, 7, 8, 9];

/**
 * 楽天レンタカー（空港受取）のURL。
 * 実測URL: cars.travel.rakuten.co.jp/cars/rcf010a.do?type=1&subtype=1&airport=TNE&gdatey=…
 * 日付は3分割param（年/月/2桁ゼロ埋めの月日）。ゼロ埋めを落とすとエラーページになるのを実測。
 * 時刻は貸出10:00／返却17:00（サイトの既定と同じ値。画面で変更可）。
 */
function rakutenCarUrl(airportCode: string, trip: TripConditions): string {
  const g = ymd(trip.departIso);
  const b = ymd(trip.returnIso);
  const p = new URLSearchParams({
    type: '1',
    subtype: '1',
    airport: airportCode,
    gdatey: g.y, gdatem: g.m, gdated: g.d, gtimeh: '10', gtimem: '00',
    bdatey: b.y, bdatem: b.m, bdated: b.d, btimeh: '17', btimem: '00',
    goflg: '0', // 出発店舗に返却（乗り捨ては先方の画面で変更できる）
  });
  const bodyTypes = RAKUTEN_CAR_BODY_TYPES.map((n) => `searchid=${n}`).join('&');
  return `https://cars.travel.rakuten.co.jp/cars/rcf010a.do?${p.toString()}&${bodyTypes}`;
}

/**
 * じゃらんレンタカー（空港受取）のURL。
 * 実測URL: jalan.net/rentacar/search/AIR_A051/plan/?rd=2026-09-10&td=2026-09-13&cc=2
 * 貸出/返却時刻は省略時に 10:00 / 18:00 が入る（実測）。cc は「○名以上」の定員フィルタ。
 */
function jalanCarUrl(airportCode: string, trip: TripConditions): string {
  const p = new URLSearchParams({ rd: trip.departIso, td: trip.returnIso, cc: String(trip.adults) });
  return `https://www.jalan.net/rentacar/search/AIR_${airportCode}/plan/?${p.toString()}`;
}

/**
 * レンタカー各社リンク。貸出＝出発日、返却＝帰着日。受取は射場の最寄り空港。
 * ※レンタカー空室の公開APIは各社とも存在しない（スクレイピングは規約違反で不可）ため、
 *   空き状況の確認は各社の検索結果ページで行ってもらう（＝条件を入れた状態で送客する）。
 *
 * @param todayIso テスト用に「今日」を差し込めるようにしてある（既定はJSTの今日）。
 */
export function rentacarLinks(site: LaunchSite, trip: TripConditions, todayIso: string = todayIsoJst()): DeepLink[] {
  const pickup: Airport | undefined = site.airports.find((a) => a.role === 'primary') ?? site.airports[0];
  const range = rangeLabel(trip);
  const at = pickup ? `${pickup.nameJa.replace(/（.*/, '')}受取` : '空港受取';
  // 楽天は在庫の登録が約8か月先までしかない。遠い日程で「プリセット」と称して0件ページに送らない。
  const leadDays = diffDays(todayIso, trip.departIso);
  const rakutenInRange = pickup != null && leadDays <= RAKUTEN_CAR_MAX_LEAD_DAYS;

  const links: DeepLink[] = [];
  links.push(
    rakutenInRange
      ? {
          serviceId: 'rakuten_rentacar',
          label: '楽天トラベル レンタカー',
          url: rakutenCarUrl(pickup!.rakutenCarCode, trip),
          prefilled: true,
          note: `${at}・${range}（10:00貸出/17:00返却）をプリセット`,
        }
      : {
          serviceId: 'rakuten_rentacar',
          label: '楽天トラベル レンタカー',
          url: 'https://travel.rakuten.co.jp/cars/',
          prefilled: false,
          note: `出発まで8か月以上あり在庫が未登録のため、条件は検索画面で入力`,
        },
  );
  /*
   * じゃらん: VCがMyLink生成時に付ける流入元コード `?vos=…` は**ここには書かない**。
   * アフィリが有効なときだけ affiliate.ts の valueCommerce ストラテジが付ける
   * （素通し時に他社の計測コード付きURLを出さないため）。
   */
  if (pickup) {
    links.push({
      serviceId: 'jalan_rentacar',
      label: 'じゃらん レンタカー',
      url: jalanCarUrl(pickup.jalanCarCode, trip),
      prefilled: true,
      note: `${at}・${range}・大人${trip.adults}名で乗れる車をプリセット`,
    });
  }
  if (pickup) {
    links.push({
      serviceId: 'tabirai_rentacar',
      label: 'たびらい レンタカー（比較）',
      url: tabiraiUrl(pickup, trip),
      prefilled: true,
      note: `${at}・${range}で各社を横断比較`,
    });
  }
  links.push(
    /*
     * トヨタ / ニッポンは条件プリセット**不可**。2026-08-01に実操作で構造まで確認した理由つき:
     *  - トヨタ … 予約は `/rakuraku/step1.aspx` の ASP.NET WebForms。`__VIEWSTATE` 前提のポストバックで、
     *    日付はhidden(`ctl00$plBody$rsv_date_start_hidden`)。GETで日付を渡しても描画に出ないのを実測。
     *  - ニッポン … 店舗検索の遷移先が緯度経度パス（`/m/nrs/<lat>_<lng>_8000/`）で日付を持たない。
     *    予約は店舗選択後のセッション遷移＝URL単体で条件を運べない。
     * どちらも「日付が入らない」ので、条件プリセットを名乗らずトップへ送る。
     */
    { serviceId: 'toyota_rentacar', label: 'トヨタレンタカー', url: 'https://rent.toyota.co.jp/', prefilled: false, note: '条件は手動入力' },
    { serviceId: 'nippon_rentacar', label: 'ニッポンレンタカー', url: 'https://www.nipponrentacar.co.jp/', prefilled: false, note: '条件は手動入力' },
  );
  return links;
}

/**
 * たびらいレンタカー（比較サイト）の検索結果URL。
 * 実測（2026-08-01）: `SYF`=貸出日 / `SYT`=返却日（いずれも yyyyMMdd）、`SYFT`/`SYTT`=時刻、
 * `SAF`/`SAT`=貸出/返却地点コード、`SAFG`/`SATG`=地点区分、`STP`=6。
 * 期間の上限は無い（+8か月でも反映される）ので楽天のような降格処理は不要。
 *
 * ⚠️ たびらいは**短時間に連続アクセスするとCloudFrontが403を返す**（レート制限）。
 *    外部リファラでも素の直接遷移でも通ることは確認済みなので、実利用者の1クリックでは問題ない。
 *    自動リンクチェッカーは403をNG扱いしないこと（check-links.ts の BOT_BLOCKED_HOSTS）。
 */
function tabiraiUrl(pickup: Airport, trip: TripConditions): string {
  const { region, code, group } = pickup.tabirai;
  const p = new URLSearchParams({
    SAFG: group, SAF: code, SATG: group, SAT: code, STP: '6',
    SYF: compactYmd(trip.departIso), SYT: compactYmd(trip.returnIso),
    SYFT: '10:00', SYTT: '17:00',
  });
  return `https://www.tabirai.net/car/${region}/search/rentacar.aspx?${p.toString()}`;
}

/**
 * ヤフーパック（宿＋航空券）の検索結果URL。
 *
 * ★2026-07-31の「条件プリセット不可（POST＋_csrf必須）」は**誤り**。フォームはPOSTだが、
 *   送信後に着地する結果ページは `/dp/<airline>/pref/<pref>/area/<wid>/?…` のGETで、直接組み立てられる。
 *   4射場×2つの日付ペアで、描画に日付・区間・エリアが出ることを確認済み（2026-08-01）。
 * ★離島・地方の射場は「空路＋宿泊」が前提なので、単品より宿とセットの方が安く付くことが多い。
 *   ANA未就航の種子島(TNE)・南紀白浜(SHM)も、JAL便のパッケージとしてここから条件つきで開ける。
 */
function yahooPackUrl(site: LaunchSite, dest: Airport, trip: TripConditions): string {
  const nights = Math.max(1, diffDays(trip.departIso, trip.returnIso));
  const p = new URLSearchParams({
    out_dep_date: compactYmd(trip.departIso),
    in_dep_date: compactYmd(trip.returnIso),
    stay_date: compactYmd(trip.departIso),
    checkout_date: compactYmd(trip.returnIso),
    out_dep_airport_cd: 'HND',
    out_arr_airport_cd: dest.iata,
    in_dep_airport_cd: dest.iata,
    in_arr_airport_cd: 'HND',
    stay_count: String(nights),
    room_count: String(trip.rooms),
    adult_count: String(trip.adults),
  });
  return `https://travel.yahoo.co.jp/dp/${dest.yahooAirline}/pref/${site.yahooPack.pref}/area/${site.yahooPack.wid}/?${p.toString()}`;
}

/**
 * ANAトラベラーズ ダイナミックパッケージ（航空券＋宿）の検索URL。
 * 日付は **yyyyMMdd**（`yyyy/MM/dd` を渡すと HTTP 400 になるのを実測）。
 *
 * ⚠️ ANA未就航の空港を渡すと「行きの到着地を入力してください。(DWRN8031)」で条件が全部落ちた
 *    空フォームになる（**HTTP 200 のまま静かに失敗する**）。呼び出し側で `anaDomtour` を必ず見ること。
 */
function anaDomtourUrl(dest: Airport, trip: TripConditions): string {
  const p = new URLSearchParams({
    CONNECTION_KIND: 'JPN', LANG: 'ja', searchCondOpenFlg: '1',
    goDeptAirpCd: 'HND', goArrAirpCd: dest.iata,
    rtnDeptAirpCd: dest.iata, rtnArrAirpCd: 'HND',
    goDeptDt: compactYmd(trip.departIso), rtnDeptDt: compactYmd(trip.returnIso),
    adultCnt: String(trip.adults),
  });
  return `https://www.ana.co.jp/domtour/booking/csm/search/DSCP0390/init?${p.toString()}`;
}

/**
 * 航空便各社リンク。origin は出発空港IATA、`dest` は到着空港（ヤフーパック/ANAが空港の属性を要るため
 * IATA文字列でなく Airport をそのまま受ける）。`site` は宿泊エリアの解決に使う。
 * 往路=出発日／復路=帰着日。
 */
export function flightLinks(
  originIata: string,
  dest: Airport,
  trip: TripConditions,
  site: LaunchSite,
): DeepLink[] {
  const o = originIata.toLowerCase();
  const d = dest.iata.toLowerCase();
  const O = originIata.toUpperCase();
  const D = dest.iata.toUpperCase();
  const skyscanner = `https://www.skyscanner.jp/transport/flights/${o}/${d}/${yymmdd(trip.departIso)}/${yymmdd(trip.returnIso)}/`;
  // 検索コード = 出発IATA + 往路DDMM + 到着IATA + 復路DDMM + 人数。末尾1桁が搭乗者数なので1〜9に収める。
  const pax = Math.min(9, Math.max(1, trip.adults));
  const aviasales = `https://www.aviasales.com/search/${O}${ddmm(trip.departIso)}${D}${ddmm(trip.returnIso)}${pax}`;
  const google = `https://www.google.com/travel/flights?q=${encodeURIComponent(`Flights from ${O} to ${D} on ${trip.departIso} through ${trip.returnIso}`)}`;
  const airlineJa = dest.yahooAirline === 'ana' ? 'ANA便' : 'JAL便';
  const links: DeepLink[] = [
    { serviceId: 'skyscanner', label: 'Skyscanner（往復比較）', url: skyscanner, prefilled: true, note: `${O}→${D}・往復日付をプリセット` },
    { serviceId: 'aviasales', label: 'Aviasales（航空券検索）', url: aviasales, prefilled: true, note: `${O}→${D}・往復日付・大人${pax}名をプリセット（英語・USD表示）` },
    { serviceId: 'google_flights', label: 'Google フライト', url: google, prefilled: true, note: '区間・日付をプリセット（参考・自然文検索）' },
    {
      serviceId: 'yahoo_package',
      label: 'ヤフーパック（宿＋航空券）',
      url: yahooPackUrl(site, dest, trip),
      prefilled: true,
      note: `${airlineJa}＋${site.nearbyAreaJa}の宿・往復日付・大人${trip.adults}名${trip.rooms}室をプリセット`,
    },
  ];
  /*
   * ANA旅作は**就航区間だけ**出す。未就航空港だとHTTP200のまま条件が全部落ちた空フォームになり、
   * 「プリセットしたのに何も入っていない」という一番たちの悪い壊れ方をするため。
   * 種子島(TNE)・南紀白浜(SHM)はANA未就航＝上のヤフーパック(JAL便)が代替になる。
   */
  if (dest.anaDomtour) {
    links.push({
      serviceId: 'ana_domtour',
      label: 'ANA 旅作（航空券＋宿）',
      url: anaDomtourUrl(dest, trip),
      prefilled: true,
      note: `${O}→${D}・往復日付・大人${trip.adults}名をプリセット`,
    });
  }
  links.push(
    { serviceId: 'ana', label: 'ANA 国内線（単品）', url: 'https://www.ana.co.jp/ja/jp/domestic/', prefilled: false, note: '区間・日付は手動入力' },
    /*
     * JALは条件プリセットの可否を**確認できていない**。jal.co.jp はこちらからのアクセスを
     * 実ブラウザでも 403（Akamaiのボット対策）で返すため、URL形式を実測できない。
     * 推測でパラメータを組むより、トップへ送って手動入力と明示する（宿つきなら上のヤフーパックがJAL便）。
     */
    { serviceId: 'jal', label: 'JAL 国内線（単品）', url: 'https://www.jal.co.jp/jp/ja/dom/', prefilled: false, note: '区間・日付は手動入力' },
  );
  return links;
}

/**
 * Googleホテルの検索語。`checkin=`/`checkout=` のような専用パラメータは**存在せず**、
 * `q=` に和文の年つき日付を混ぜる形でしか日付が入らない（2026-08-01実測）。
 *
 * ⚠️ 静かに日付だけが落ちる条件が3つある。いずれもHTTP200・場所は正しく解決するので画面上は正常に見える:
 *   1. 射場名の括弧（「（吉信射点 LA-Y）」等）を残す → 日付が既定（今日+1泊）に戻る。4射場で再現。
 *   2. 区切りが `-` / 半角空白 → 3泊が1泊に化ける。ISO形式 → 丸1か月（9/1〜9/30）に化ける。
 *   3. `checkin=2026-09-10` のような専用パラメータ → 完全に黙殺される。
 *   → 括弧を落とした名前 ＋ 和文日付 ＋ 区切りは波ダッシュ U+301C のみ、が唯一通る形。
 * ⚠️ 「内之浦」単独は東京都世田谷区に化けるため、必ず正式名称（内之浦宇宙空間観測所）を使う。
 */
function googleHotelQuery(site: LaunchSite, trip: TripConditions): string {
  // '〜' = 波ダッシュ。全角チルダ(U+FF5E)と見分けが付かないのでエスケープで書く。
  return `${shortSiteName(site)} 周辺 ホテル ${jaDate(trip.departIso)}\u301C${jaDate(trip.returnIso)}`;
}

/**
 * 楽天トラベル以外で周辺の宿を探すリンク。
 * 上の一覧（/api/hotels）は楽天の在庫なので、ここでは**別の在庫**へ条件つきで送る。
 */
export function otherHotelLinks(site: LaunchSite, trip: TripConditions): DeepLink[] {
  const range = rangeLabel(trip);
  const guests = `大人${trip.adults}名${trip.rooms > 1 ? `${trip.rooms}室` : '1室'}`;
  const a = site.jalanArea;
  /*
   * じゃらん（宿泊）: /{kenCd}/LRG_{lrgCd}/[SML_{smlCd}/]?stayYear=…
   * ★種子島の SML を省くと LRG「離島」＝奄美・屋久島・徳之島まで静かに混ざる（唯一の静かな失敗経路）。
   * ★月は 9（ゼロ埋めなし）で実測している。ISOの "09" をそのまま渡さず Number() を通す。
   */
  const jalanPath = `/${a.kenCd}/LRG_${a.lrgCd}/${a.smlCd ? `SML_${a.smlCd}/` : ''}`;
  const j = ymd(trip.departIso);
  const jalanQuery = new URLSearchParams({
    stayYear: j.y,
    stayMonth: String(Number(j.m)),
    stayDay: String(Number(j.d)),
    stayCount: String(nights(trip)),
    adultNum: String(trip.adults),
    roomCount: String(trip.rooms),
  });
  /*
   * るるぶトラベル（JTB系・実体はAgodaホワイトラベル）。楽天に無い民宿・ゲストハウスがこちらに
   * あることがあり、離島・過疎地の射場では実利がある。
   * ★`/city/japan/<slug>` ページは日付クエリを受け付けない（200のまま日付が入らない）ので
   *   `/search?city=<数値ID>` を使う。city を省くとトップへリダイレクトされる＝IDは必須。
   */
  const rurubuQuery = new URLSearchParams({
    city: String(site.rurubuCityId),
    checkIn: trip.departIso,
    los: String(nights(trip)),
    rooms: String(trip.rooms),
    adults: String(trip.adults),
    children: '0',
  });
  return [
    {
      serviceId: 'rurubu_hotel',
      label: 'るるぶトラベルで探す',
      url: `https://www.rurubu.travel/search?${rurubuQuery.toString()}`,
      prefilled: true,
      note: `${site.nearbyAreaJa}エリア・${range}・${guests}で空室のある宿（楽天とは別の在庫）`,
    },
    {
      serviceId: 'jalan_hotel',
      label: 'じゃらんで探す',
      url: `https://www.jalan.net${jalanPath}?${jalanQuery.toString()}`,
      prefilled: true,
      note: `${site.nearbyAreaJa}エリア・${range}・${guests}をプリセット（国内最大級の在庫）`,
    },
    {
      serviceId: 'google_hotels',
      label: 'Googleホテル（料金比較）',
      url: `https://www.google.com/travel/search?q=${encodeURIComponent(googleHotelQuery(site, trip))}`,
      prefilled: true,
      note: `${range}をプリセットして料金を横断比較（人数は指定できず大人2名・1室で開きます）`,
    },
    {
      serviceId: 'google_maps_hotels',
      label: 'Googleマップで周辺ホテル',
      /*
       * ★検索語に日付を混ぜてはいけない。HTTP200のまま結果0件になり、
       *   宿が実在する種子島でも「周辺に宿が無い」ように見える（実測2026-08-01）。
       */
      url: `https://www.google.com/maps/search/${encodeURIComponent('ホテル')}/@${site.lat},${site.lon},12z`,
      prefilled: false,
      note: '射場周辺の宿を地図で表示（日付はURLに載せられません）',
    },
  ];
}

/**
 * 周辺の見どころ・体験（アクティビティ予約への送客）。
 * どちらも「参加日1日＋人数」までしか渡せない（日帰り体験の商材なのでレンジ指定UIが無い）。
 * → サイト内の**出発日**を参加日として渡す。
 *
 * ★エリアは県止まりではなく射場周辺まで絞る（2026-08-01に実採取）。
 *   従来は鹿児島県全体で、種子島に行く人に桜島や奄美大島の体験が混ざっていた。
 * ⚠️ 静かな失敗の作り分け:
 *   - アクティビティジャパン … エリアが**URLパス**なので、日付が壊れてもエリアは残る（全国化しない）。
 *     ただし不正な日付（99999999や過去日）を検証せずそのまま表示するので、妥当な未来日を出す責任はこちら側。
 *   - アソビュー … エリアが**クエリ**なので、destinationId が空だと静かに「すべての行き先 4,013件」に化ける。
 *     ID が無いときはリンク自体を出さない（下のガード）。
 *
 * @param trip 日付が確定していない射場ガイドから呼ぶときは null（推測で日付を入れない）。
 */
export function activityLinks(site: LaunchSite, trip: TripConditions | null): DeepLink[] {
  const when = trip ? `${Number(ymd(trip.departIso).m)}/${Number(ymd(trip.departIso).d)}着` : '';
  const links: DeepLink[] = [];

  const ajQuery = trip
    ? `?${new URLSearchParams({ joinDayFromCalendar: compactYmd(trip.departIso), searchPeople: String(trip.adults) }).toString()}`
    : '';
  links.push({
    serviceId: 'activityjapan',
    label: 'アクティビティジャパン',
    url: `https://activityjapan.com/search/${site.activityJapanPath}/${ajQuery}`,
    prefilled: true,
    note: trip
      ? `${site.nearbyAreaJa}エリア・${when}・${trip.adults}名で参加できる体験`
      : `${site.nearbyAreaJa}エリアの体験・アクティビティ`,
  });

  // destinationId が空のURLは全国結果に化けるので、その場合はリンクを出さない（誤誘導しない）。
  if (site.asoviewAreaId) {
    const asoQuery = new URLSearchParams({ destinationId: site.asoviewAreaId, destinationType: 'area' });
    if (trip) {
      asoQuery.set('experienceDate', trip.departIso);
      asoQuery.set('adultQuantity', String(trip.adults));
    }
    links.push({
      serviceId: 'asoview',
      label: 'アソビュー！',
      url: `https://www.asoview.com/search/?${asoQuery.toString()}`,
      prefilled: true,
      note: trip
        ? `${site.nearbyAreaJa}エリア・${when}・大人${trip.adults}名で参加できる体験`
        : `${site.nearbyAreaJa}エリアの体験・アクティビティ`,
    });
  }
  return links;
}
